// message-progress.ts — CE QU'UN STATUT DIT DU PARCOURS D'UN MESSAGE (§19), et rien d'autre.
//
// ⚠️ POURQUOI CETTE RÈGLE VIT SEULE. Le volet du journal fait DEUX choses avec le même fait — « où en est ce
// message ? » : il dessine une progression en quatre étapes, et il se relit tant qu'une suite est attendue.
// Les deux vivaient en ligne dans le rendu, donc hors de portée de tout banc, alors que leur mode de panne
// est muet : un statut qu'elles ne connaissent pas rend une progression VIDE *et* arrête la relecture — le
// volet se figerait sur un message qui avance, sans la moindre erreur (§23.0).
//
// ⚠️ ELLES NE DISENT RIEN DE CE QU'ON PEUT FAIRE : « ce message est-il rejouable » appartient au SERVEUR
// (`replayable`, cf. `admin/replay.ts`). Une seconde liste de statuts ici l'aurait contredit en silence.

/**
 * Étape atteinte, de 0 à 4.
 *
 * ⚠️ `misrouted` et `duplicate` SONT À L'ÉTAPE 2 : ces deux messages ont bel et bien été TRADUITS, puis
 * retenus — l'un parce qu'il vise un autre magasin que son dossier (§5.1), l'autre parce que le document
 * était déjà intégré (§7bis). Les ramener à l'étape 1 ferait croire que la traduction a échoué, et c'est
 * justement ce qu'un opérateur irait vérifier en premier.
 * ⚠️ `error` et `unrecognized` restent à 1 : le message est RELEVÉ, rien de plus.
 */
const STEP_REACHED: Record<string, number> = {
  received: 1,
  unrecognized: 1,
  error: 1,
  translated: 2,
  misrouted: 2,
  duplicate: 2,
  pushed: 3,
  confirmed: 4,
};

/**
 * ÉTATS EN TRANSIT — ceux dont on SAIT qu'une suite est attendue, sans action humaine.
 *
 * ⚠️ 🔴 POURQUOI LE VOLET SE RELIT : le rejeu est UN SEUL appel, qui rend le statut atteint par la
 * re-traduction (`translated`). L'envoi au back-office, lui, est traité par un worker — il n'y a donc
 * aucune seconde réponse vers le navigateur. Sans relecture, le panneau affichait « Traduit » alors que la
 * base disait déjà `confirmed` : pas un état INVENTÉ (il est dérivé du statut réel), mais un état qui a
 * CESSÉ D'ÊTRE VRAI. C'est la forme discrète du §23.0, et elle trompe autant — « Poussé au BO » en gris se
 * lit comme un blocage.
 * ⚠️ On s'arrête aux états TERMINAUX : relire indéfiniment une erreur ou un `confirmed` ne rapporterait
 * rien et ferait travailler le serveur pour rien. Un état qui attend un HUMAIN (erreur, mauvais magasin)
 * est terminal de ce point de vue — c'est le rejeu qui le fera repartir.
 */
const IN_TRANSIT = new Set(['received', 'translated', 'pushed']);

/** Étape atteinte par ce statut (0 = inconnu : aucune étape n'est cochée, plutôt qu'une étape devinée). */
export function reachedStep(status: string): number {
  return STEP_REACHED[status] ?? 0;
}

/** Une suite est-elle attendue sans action humaine ? (c'est ce qui décide de relire ou de s'arrêter) */
export function isInTransit(status: string): boolean {
  return IN_TRANSIT.has(status);
}

/** Les statuts dont la progression est connue — lu par le banc de couverture, jamais par un écran. */
export function statusesWithStep(): string[] {
  return Object.keys(STEP_REACHED);
}
